import { createContext, useEffect, useState, ReactNode } from 'react';
import { Spin } from 'antd';
import Keycloak from 'keycloak-js';
import keycloak from './keycloakConfig';

interface KeycloakContextType {
  keycloak: Keycloak | null;
  authenticated: boolean;
  initialized: boolean;
  userRole: string | null;
}

export const KeycloakContext = createContext<KeycloakContextType>({
  keycloak: null,
  authenticated: false,
  initialized: false,
  userRole: null,
});

interface KeycloakProviderProps {
  children: ReactNode;
}

export const KeycloakProvider = ({ children }: KeycloakProviderProps) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const initKeycloak = async () => {
      try {
        const auth = await keycloak.init({
          //onLoad: 'check-sso',
          onLoad: 'login-required',
          pkceMethod: 'S256',
          checkLoginIframe: false,
        });

        setAuthenticated(auth);

        // Fetch the user's DB role so permission checks work even when
        // the Keycloak realm role assignment failed during approval.
        if (auth && keycloak.token) {
          try {
            const res = await fetch('/api/v1/users/me', {
              headers: { Authorization: `Bearer ${keycloak.token}` },
            });
            if (res.ok) {
              const user = await res.json();
              setUserRole(user.role ?? null);
            }
          } catch (err) {
            console.error('Failed to fetch user profile for role', err);
          }
        }

        setInitialized(true);

        // Setup token refresh
        if (auth) {
          setInterval(() => {
            keycloak
              .updateToken(70)
              .then((refreshed) => {
                if (refreshed) {
                  console.log('Token refreshed');
                }
              })
              .catch(() => {
                console.error('Failed to refresh token');
                keycloak.login();
              });
          }, 60000); // Check every minute
        }
      } catch (error) {
        console.error('Keycloak initialization failed', error);
        setInitialized(true);
      }
    };

    initKeycloak();
  }, []);

  if (!initialized) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" tip="Initializing authentication..." />
      </div>
    );
  }

  return (
    <KeycloakContext.Provider value={{ keycloak, authenticated, initialized, userRole }}>
      {children}
    </KeycloakContext.Provider>
  );
};
